import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Check, Code2, Zap, Shield, Globe, Send, Loader2, CreditCard, Mail, DollarSign, Sparkles, Wallet, Lock } from "lucide-react";
import { toast } from "sonner";
import { fetchPlans, requestApiKey, createCreemCheckout, createCreemSubscription } from "@/lib/api";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useLang } from "@/i18n";

export default function Marketplace() {
  const { lang, t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const CREDIT_RATIO = 500;
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [chosenPlan, setChosenPlan] = useState("free");
  const [form, setForm] = useState({ name: "", email: "", company: "", use_case: "" });
  const [submitting, setSubmitting] = useState(false);
  const [generatedKey, setGeneratedKey] = useState(null);
  const [sp, setSp] = useSearchParams();
  const [customAmount, setCustomAmount] = useState("29");
  const [customLoading, setCustomLoading] = useState(false);

  useEffect(() => {
    fetchPlans().then(setPlans).catch(console.error);
  }, []);

  useEffect(() => {
    if (sp.get("paid") === "1") {
      toast.success(lang === "tr" ? "Ödeme başarılı! Aboneliğin aktif." : "Payment successful! Your subscription is active.");
      sp.delete("paid");
      setSp(sp, { replace: true });
    }
  }, [sp, setSp, lang]);

  const requireLogin = (intent) => {
    toast.error(lang === "tr" ? "Önce giriş yapmalısın" : "Please sign in first");
    navigate(`/login?redirect=${encodeURIComponent("/pricing")}`);
  };

  const handlePlanClick = (planId) => {
    if (planId === "free") {
      if (!user) { requireLogin(planId); return; }
      setChosenPlan(planId);
      setGeneratedKey(null);
      setForm({ name: user.name || user.email.split("@")[0], email: user.email, company: "", use_case: "" });
      setOpen(true);
      return;
    }
    if (!user) { requireLogin(planId); return; }
    if (planId === "pro" || planId === "enterprise") {
      setCustomLoading(true);
      createCreemSubscription(planId)
        .then((res) => { window.location.assign(res.checkout_url); })
        .catch((err) => {
          const msg = formatApiError(err?.response?.data?.detail) || err?.message || "Unknown error";
          toast.error(`${lang === "tr" ? "Hata" : "Error"}: ${String(msg).slice(0, 140)}`);
          setCustomLoading(false);
        });
      return;
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) return;
    setSubmitting(true);
    try {
      const res = await requestApiKey({
        name: form.name,
        company: form.company,
        use_case: form.use_case,
        plan: chosenPlan,
      });
      setGeneratedKey(res.api_key);
      toast.success(t("form_sent"));
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail) || (lang === "tr" ? "Anahtar üretilemedi" : "Error generating key");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCustomPay = async (e) => {
    e.preventDefault();
    const amt = parseFloat(customAmount);
    if (isNaN(amt) || amt < 1 || amt > 10000) {
      toast.error(lang === "tr" ? "Tutar $1 ile $10,000 arasında olmalı" : "Amount must be between $1 and $10,000");
      return;
    }
    if (!user) { requireLogin("custom"); return; }
    setCustomLoading(true);
    try {
      const res = await createCreemCheckout(amt);
      if (!res.checkout_url) throw new Error("No checkout URL");
      window.location.assign(res.checkout_url);
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail) || err?.message || "Unknown error";
      toast.error(`${lang === "tr" ? "Hata" : "Error"}: ${String(msg).slice(0, 140)}`);
      setCustomLoading(false);
    }
  };

  return (
    <div data-testid="marketplace-page">
      {/* HERO */}
      <section className="bg-neutral-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: "radial-gradient(circle at 20% 30%, rgba(230,57,70,0.4) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(230,57,70,0.2) 0%, transparent 50%)",
        }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="eyebrow text-red-500" data-testid="market-eyebrow">{t("market_hero_eyebrow")}</span>
            <h1 className="headline text-5xl sm:text-6xl lg:text-7xl mt-3 text-balance leading-[1.05]" data-testid="market-hero-title">
              {t("market_hero_title")}
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-neutral-300 max-w-2xl leading-relaxed">
              {t("market_hero_desc")}
            </p>
            <p className="mt-4 text-base text-neutral-300 max-w-2xl leading-relaxed">
              {t("market_hero_desc_2")}
            </p>
            <p className="mt-4 text-sm text-neutral-400 max-w-2xl leading-relaxed" data-testid="market-clarification">
              {t("market_source_note")} {t("market_portal_note")}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <button onClick={() => handlePlanClick("free")} className="bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wider text-sm px-6 py-3 rounded-sm transition-colors" data-testid="market-cta-start">
                {t("market_cta_start")}
              </button>
              <Link to="/marketplace/docs" className="border border-neutral-700 hover:border-white text-white font-bold uppercase tracking-wider text-sm px-6 py-3 rounded-sm transition-colors" data-testid="market-cta-docs">
                {t("market_cta_docs")}
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-sm text-neutral-400">
              <span className="flex items-center gap-2"><Shield size={16} className="text-red-500" /> {lang === "tr" ? "Manuel editoryal onay" : "Manual editorial review"}</span>
              <span className="flex items-center gap-2"><Sparkles size={16} className="text-red-500" /> {lang === "tr" ? "AI destekli üretim" : "AI-assisted generation"}</span>
              <span className="flex items-center gap-2"><Globe size={16} className="text-red-500" /> TR · EN</span>
              <span className="flex items-center gap-2"><Code2 size={16} className="text-red-500" /> RESTful JSON</span>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-neutral-900 border border-neutral-800 rounded-sm overflow-hidden font-mono text-sm">
              <div className="flex items-center gap-2 px-4 py-2 border-b border-neutral-800 bg-neutral-950">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
                <span className="ml-2 text-neutral-500 text-xs">curl</span>
              </div>
              <pre className="p-4 text-neutral-300 leading-relaxed overflow-x-auto">
{`$ curl https://loftpress.co/api/news \\
   -H "Authorization: Bearer lp_pro_..." \\
   -G -d "lang=tr" -d "category=technology"

{
  "items": [
    {
      "id": "cnt_20260722_da64ae04",
      "title": "Bilgisayarlarda RAM nasıl çalışır?",
      "summary": "RAM sisteminin temel işlevlerini açıklayan editoryal içerik.",
      "category": "technology",
      "language": "tr",
      "content_type": "article",
      "origin": "loftpress_generated",
      "editorial_status": "approved",
      "published_at": "2026-07-22T10:00:00Z"
    }
  ]
}`}
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* CUSTOM AMOUNT — PAY WHAT YOU WANT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20" data-testid="custom-amount-section">
        <div className="relative overflow-hidden rounded-sm border-2 border-red-600 bg-gradient-to-br from-neutral-950 via-neutral-950 to-red-950/30 p-8 sm:p-12">
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-red-600/10 blur-3xl" />
          <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-red-600/10 blur-3xl" />

          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-red-600/15 border border-red-600/30 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest text-red-400">
                <Sparkles size={14} /> {lang === "tr" ? "ESNEK API KULLANIMI" : "FLEXIBLE API USAGE"}
              </div>
              <h2 className="headline text-4xl sm:text-5xl mt-4 text-white text-balance">
                {t("custom_title")}
              </h2>
              <p className="mt-4 text-base sm:text-lg text-neutral-300 leading-relaxed max-w-xl">
                {t("custom_desc")}
              </p>
              <p className="mt-3 text-xs font-mono uppercase tracking-wider text-neutral-500">
                {t("custom_min_max")}
              </p>
            </div>

            <form onSubmit={handleCustomPay} className="bg-black/60 backdrop-blur-md border border-neutral-800 rounded-sm p-6 space-y-4" data-testid="custom-pay-form">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-400 block mb-2">
                  {t("custom_amount_label")} (USD)
                </label>
                <div className="relative">
                  <DollarSign size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-red-500" />
                  <input
                    type="number"
                    min="1"
                    max="10000"
                    step="1"
                    required
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    placeholder="29"
                    className="w-full bg-neutral-900 border border-neutral-800 text-white text-2xl headline rounded-sm pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-600"
                    data-testid="custom-amount-input"
                  />
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[10, 29, 50, 100, 250, 500].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setCustomAmount(String(v))}
                      className={`px-3 py-1 text-xs font-mono rounded-sm border transition-colors ${
                        Number(customAmount) === v
                          ? "bg-red-600 border-red-600 text-white"
                          : "border-neutral-800 text-neutral-400 hover:border-red-500 hover:text-red-500"
                      }`}
                      data-testid={`quick-amount-${v}`}
                    >
                      ${v}
                    </button>
                  ))}
                </div>
              </div>

              {/* Credit preview */}
              <div className="bg-gradient-to-r from-red-950/30 to-neutral-900 border border-red-900/50 rounded-sm p-4 flex items-center gap-3" data-testid="credit-preview">
                <Wallet className="text-red-500 flex-shrink-0" size={22} />
                <div className="flex-1">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">
                    {lang === "tr" ? "BU TUTARLA ALACAĞIN" : "USAGE UNITS YOU WILL RECEIVE"}
                  </p>
                  <p className="headline text-2xl text-white">
                    {(Number(customAmount || 0) * CREDIT_RATIO).toLocaleString()}{" "}
                    <span className="text-sm text-red-400 font-mono uppercase tracking-wider">
                      {lang === "tr" ? "API Kullanım Birimi" : "API Usage Units"}
                    </span>
                  </p>
                </div>
                <p className="text-[10px] font-mono text-neutral-600 hidden sm:block text-right leading-tight">
                  $1 = {CREDIT_RATIO}<br />{lang === "tr" ? "kullanım birimi" : "usage units"}
                </p>
              </div>

              {user ? (
                <button
                  type="submit"
                  disabled={customLoading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-4 rounded-sm uppercase tracking-wider font-bold text-base transition-colors disabled:opacity-60"
                  data-testid="custom-pay-btn"
                >
                  {customLoading ? (
                    <><Loader2 size={18} className="animate-spin" /> {t("custom_loading")}</>
                  ) : (
                    lang === "tr" ? (
                      <><CreditCard size={18} /> {(Number(customAmount || 0) * CREDIT_RATIO).toLocaleString()} KULLANIM BİRİMİ SATIN AL — ${customAmount || "0"}</>
                    ) : (
                      <><CreditCard size={18} /> PURCHASE {(Number(customAmount || 0) * CREDIT_RATIO).toLocaleString()} USAGE UNITS — ${customAmount || "0"}</>
                    )
                  )}
                </button>
              ) : (
                <Link
                  to={`/login?redirect=${encodeURIComponent("/pricing")}`}
                  className="w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-4 rounded-sm uppercase tracking-wider font-bold text-base transition-colors"
                  data-testid="custom-pay-login-btn"
                >
                  <Lock size={18} /> {lang === "tr" ? "Ödemek için Giriş Yap" : "Sign in to Pay"}
                </Link>
              )}

              <p className="text-[11px] font-mono uppercase tracking-wider text-neutral-500 text-center">
                {lang === "tr"
                  ? "Tek seferlik ödeme · Paddle üzerinden güvenli şekilde işlenir"
                  : "One-time payment · Securely processed through Paddle"}
              </p>
              <p className="text-[10px] text-neutral-600 text-center leading-relaxed">
                {lang === "tr"
                  ? "Kullanım birimleri yalnızca Loft Press Content Production API üzerinden yapılan isteklerde kullanılabilir."
                  : "Usage units may only be used for requests made through the Loft Press Content Production API."}
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="pricing-section">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="eyebrow text-red-500">PRICING</span>
          <h2 className="headline text-4xl sm:text-5xl mt-3 text-white">{t("market_pricing_title")}</h2>
          <p className="mt-4 text-lg text-neutral-400">{t("market_pricing_desc")}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {plans.map((p) => (
            <div
              key={p.id}
              className={`relative border-2 rounded-sm p-8 flex flex-col ${
                p.highlight ? "border-red-600 bg-neutral-950 text-white shadow-2xl shadow-red-900/30 scale-[1.02]" : "border-neutral-800 bg-neutral-950 text-white"
              }`}
              data-testid={`plan-${p.id}`}
            >
              {p.highlight && (
                <span className="absolute -top-3 left-8 bg-red-600 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-sm">
                  Most Popular
                </span>
              )}
              <h3 className="headline text-3xl">{p.name}</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="headline text-5xl">{p.price_label}</span>
                <span className="text-sm text-neutral-400">{t("market_per_month")}</span>
              </div>
              <p className={`mt-2 text-sm font-mono uppercase tracking-wider ${p.highlight ? "text-red-400" : "text-neutral-500"}`}>
                {p.requests}
              </p>

              <ul className="mt-8 space-y-3 flex-1">
                {p.features.map((f, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-neutral-300">
                    <Check size={18} className={`flex-shrink-0 mt-0.5 ${p.highlight ? "text-red-400" : "text-red-500"}`} />
                    <span>{lang === "tr" && f === "Customer-exclusive content" ? "Müşteriye özel içerik" : f}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handlePlanClick(p.id)}
                className={`mt-8 w-full py-3 rounded-sm font-bold uppercase tracking-wider text-sm transition-colors inline-flex items-center justify-center gap-2 ${
                  p.highlight
                    ? "bg-red-600 hover:bg-red-700 text-white"
                    : "bg-white hover:bg-red-600 text-black hover:text-white"
                }`}
                data-testid={`plan-cta-${p.id}`}
              >
                {(p.id === "pro" || p.id === "enterprise") && <CreditCard size={16} />}
                {p.id === "pro"
                  ? (lang === "tr" ? "Şimdi Abone Ol" : "Subscribe Now")
                  : p.id === "enterprise"
                  ? (lang === "tr" ? "Enterprise'a Geç" : "Subscribe to Enterprise")
                  : p.cta}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="bg-neutral-950 border-y border-neutral-800 py-20" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="eyebrow text-red-500">CAPABILITIES</span>
            <h2 className="headline text-4xl sm:text-5xl mt-3 text-white">
              {lang === "tr" ? "Tek API, çok formatlı içerik." : "One API, multi-format content."}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { i: <Sparkles size={24} />, t_tr: "AI destekli üretim", t_en: "AI-assisted generation", d_tr: "Operatör konu ve başlıklarından yeni içerik oluşturulur.", d_en: "New content created from operator topics and titles." },
              { i: <Shield size={24} />, t_tr: "Manuel editoryal onay", t_en: "Manual editorial review", d_tr: "Her içerik yayın öncesi elle incelenir ve onaylanır.", d_en: "Every item reviewed and approved before publication." },
              { i: <Globe size={24} />, t_tr: "İki dil desteği", t_en: "Bilingual", d_tr: "Türkçe ve İngilizce içerikler.", d_en: "Turkish and English content." },
              { i: <Code2 size={24} />, t_tr: "Çok formatlı", t_en: "Multi-format", d_tr: "Metin bugün; görsel, ses, video yakında.", d_en: "Text today; image, audio, video coming soon." },
            ].map((f, idx) => (
              <div key={idx} className="bg-black border border-neutral-800 p-6 rounded-sm" data-testid={`feature-${idx}`}>
                <div className="w-10 h-10 bg-red-600 text-white flex items-center justify-center rounded-sm">{f.i}</div>
                <h4 className="headline text-xl mt-4 text-white">{lang === "tr" ? f.t_tr : f.t_en}</h4>
                <p className="mt-2 text-sm text-neutral-400 leading-relaxed">{lang === "tr" ? f.d_tr : f.d_en}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST / CONTACT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="trust-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <span className="eyebrow text-red-500">WHAT WE SELL</span>
            <h2 className="headline text-4xl sm:text-5xl mt-3 text-white text-balance">
              {lang === "tr" ? "Özgün içerik üretim hizmeti." : "Original content generation service."}
            </h2>
            <p className="mt-5 text-base text-neutral-300 leading-relaxed max-w-xl">
              {t("market_source_note")} {t("market_portal_note")}
            </p>
            <ul className="mt-6 space-y-2 text-sm text-neutral-300">
              <li className="flex items-start gap-2"><Check size={16} className="text-red-500 mt-0.5 flex-shrink-0" />{lang === "tr" ? "Loft Press üretimi içerikler" : "Loft Press-generated content"}</li>
              <li className="flex items-start gap-2"><Check size={16} className="text-red-500 mt-0.5 flex-shrink-0" />{lang === "tr" ? "Otomatik güvenlik + manuel onay" : "Automated safety + manual approval"}</li>
              <li className="flex items-start gap-2"><Check size={16} className="text-red-500 mt-0.5 flex-shrink-0" />{lang === "tr" ? "İstediğin zaman iptal" : "Cancel anytime"}</li>
              <li className="flex items-start gap-2"><Check size={16} className="text-red-500 mt-0.5 flex-shrink-0" />{lang === "tr" ? "Deepfake, ses klonlama, gerçek kişi taklidi yok" : "No deepfakes, voice cloning or real-person impersonation"}</li>
            </ul>
            <p className="mt-6 text-xs text-neutral-500 leading-relaxed max-w-xl">
              {lang === "tr"
                ? "Loft Press eğitsel bilim ve anatomi içerikleri (örneğin konuşan bir kalbin temel biyolojik işlevini anlatması) oluşturabilir. Bu içerikler yalnızca eğitim amaçlıdır; teşhis, tedavi, ilaç önerisi veya kişiselleştirilmiş tıbbi tavsiye içermez. Loft Press ayrıca arama motoru sıralaması, trafik veya ticari sonuç garantisi vermez."
                : "Loft Press may create general educational science and anatomy content (e.g. a fictional talking heart explaining its basic biological function). Such content is informational only and does not provide diagnosis, treatment, medication recommendations or personalized medical advice. Loft Press does not guarantee search rankings, traffic or commercial results."}
            </p>
          </div>

          <div className="bg-neutral-950 border border-neutral-800 rounded-sm p-8">
            <h3 className="headline text-2xl text-white">{lang === "tr" ? "İletişim" : "Contact"}</h3>
            <p className="mt-2 text-sm text-neutral-400">
              {lang === "tr"
                ? "Sorularınız için ekibimize ulaşabilirsiniz. Genelde 24 saat içinde yanıtlıyoruz."
                : "Reach out to our team for any question. We typically reply within 24 hours."}
            </p>
            <dl className="mt-6 space-y-3 text-sm">
              {[
                ["Support", "support@loftpress.co"],
                ["Billing", "billing@loftpress.co"],
                ["Privacy", "privacy@loftpress.co"],
                ["Legal", "legal@loftpress.co"],
              ].map(([label, addr]) => (
                <div key={addr} className="flex items-center justify-between border-b border-neutral-800 pb-2">
                  <dt className="font-mono uppercase tracking-wider text-xs text-neutral-500">{label}</dt>
                  <dd>
                    <a href={`mailto:${addr}`} className="font-mono text-sm text-red-400 hover:text-red-300" data-testid={`contact-${label.toLowerCase()}`}>{addr}</a>
                  </dd>
                </div>
              ))}
            </dl>
            <div className="mt-6 flex flex-wrap gap-3 text-xs">
              <Link to="/terms" className="px-3 py-1 border border-neutral-800 rounded-sm text-neutral-400 hover:text-red-500 hover:border-red-500 transition-colors">Terms</Link>
              <Link to="/privacy" className="px-3 py-1 border border-neutral-800 rounded-sm text-neutral-400 hover:text-red-500 hover:border-red-500 transition-colors">Privacy</Link>
              <Link to="/refund" className="px-3 py-1 border border-neutral-800 rounded-sm text-neutral-400 hover:text-red-500 hover:border-red-500 transition-colors">Refund</Link>
            </div>
          </div>
        </div>
      </section>

      {/* API Key Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="api-key-dialog">
          <DialogHeader>
            <DialogTitle className="headline text-2xl">{t("market_form_title")}</DialogTitle>
            <DialogDescription>{t("market_form_desc")}</DialogDescription>
          </DialogHeader>

          {generatedKey ? (
            <div className="space-y-4" data-testid="api-key-success">
              <div className="bg-green-950/50 border border-green-800 p-4 rounded-sm">
                <p className="text-sm font-semibold text-green-300">{t("form_sent")}</p>
                <p className="mt-1 text-xs text-green-400">
                  {lang === "tr"
                    ? "Anahtarı hemen kaydet — bu ekranı kapattığında bir daha gösterilmeyecek."
                    : "Save this key now — it will not be shown again after you close this screen."}
                </p>
              </div>
              <div className="bg-black text-green-400 font-mono text-xs p-4 rounded-sm break-all border border-neutral-800" data-testid="api-key-value">
                {generatedKey}
              </div>
              <button
                onClick={() => { setOpen(false); setForm({ name: "", email: "", company: "", use_case: "" }); }}
                className="w-full bg-white hover:bg-red-600 text-black hover:text-white py-3 rounded-sm uppercase tracking-wider font-bold text-sm transition-colors"
                data-testid="api-key-close"
              >
                {lang === "tr" ? "Kaydettim, kapat" : "I saved it, close"}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">{t("form_name")}</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full bg-neutral-900 border border-neutral-800 text-white rounded-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-600"
                  data-testid="form-name-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">{t("form_email")}</label>
                <input
                  readOnly
                  value={user?.email || ""}
                  className="mt-1 w-full bg-neutral-900 border border-neutral-800 text-neutral-400 rounded-sm px-3 py-2 focus:outline-none cursor-not-allowed"
                  data-testid="form-email-input"
                />
                <p className="mt-1 text-[10px] font-mono uppercase tracking-wider text-neutral-500">
                  {lang === "tr" ? "Hesabına bağlanır" : "Bound to your account"}
                </p>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">{t("form_company")}</label>
                <input
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="mt-1 w-full bg-neutral-900 border border-neutral-800 text-white rounded-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-600"
                  data-testid="form-company-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">{t("form_plan")}</label>
                <Select value={chosenPlan} onValueChange={setChosenPlan}>
                  <SelectTrigger className="mt-1" data-testid="form-plan-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {plans.map((p) => (
                      <SelectItem key={p.id} value={p.id} data-testid={`plan-option-${p.id}`}>
                        {p.name} — {p.price_label}{t("market_per_month")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">{t("form_use_case")}</label>
                <textarea
                  rows={3}
                  value={form.use_case}
                  onChange={(e) => setForm({ ...form, use_case: e.target.value })}
                  className="mt-1 w-full bg-neutral-900 border border-neutral-800 text-white rounded-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-600"
                  data-testid="form-usecase-input"
                />
              </div>
              <DialogFooter>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-3 rounded-sm uppercase tracking-wider font-bold text-sm transition-colors disabled:opacity-60"
                  data-testid="form-submit-btn"
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  {t("form_submit")}
                </button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
